/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Directive,
  ElementRef,
  Renderer2,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  QueryList,
  ViewEncapsulation,
  forwardRef,
  ViewChild,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import {NG_VALUE_ACCESSOR, ControlValueAccessor} from '@angular/forms';
import {
  RippleRef,
  UniqueSelectionDispatcher,
  MdRipple,
  FocusOriginMonitor,
  FocusOrigin,
} from '../core';
import {coerceBooleanProperty} from '../core/coercion/boolean-property';
import {mixinDisabled, CanDisable} from '../core/common-behaviors/disabled';
import {CanColor, mixinColor} from '../core/common-behaviors/color';
/**
 * Provider Expression that allows md-radio-group to register as a ControlValueAccessor. This
 * allows it to support [(ngModel)] and ngControl.
 * \@docs-private
 */
export const MD_RADIO_GROUP_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MdRadioGroup),
  multi: true
};

let /** @type {?} */ _uniqueIdCounter = 0;
/**
 * Change event object emitted by MdRadio and MdRadioGroup.
 */
export class MdRadioChange {
/**
 * The MdRadioButton that emits the change event.
 */
source: MdRadioButton;
/**
 * The value of the MdRadioButton.
 */
value: any;
}

function MdRadioChange_tsickle_Closure_declarations() {
/**
 * The MdRadioButton that emits the change event.
 * @type {?}
 */
MdRadioChange.prototype.source;
/**
 * The value of the MdRadioButton.
 * @type {?}
 */
MdRadioChange.prototype.value;
}

export class MdRadioGroupBase { }
export const /** @type {?} */ _MdRadioGroupMixinBase = mixinDisabled(MdRadioGroupBase);
/**
 * A group of radio buttons. May contain one or more `<md-radio-button>` elements.
 */
export class MdRadioGroup extends _MdRadioGroupMixinBase
    implements AfterContentInit, ControlValueAccessor, CanDisable {
/**
 * Selected value for group. Should equal the value of the selected radio button if there *is*
 * a corresponding radio button with a matching value. If there is *not* such a corresponding
 * radio button, this value persists to be applied in case a new radio button is added with a
 * matching value.
 */
private _value: any = null;
/**
 * The HTML name attribute applied to radio buttons in this group.
 */
private _name: string = `md-radio-group-${_uniqueIdCounter++}`;
/**
 * The currently selected radio button. Should match value.
 */
private _selected: MdRadioButton = null;
/**
 * Whether the `value` has been set to its initial value.
 */
private _isInitialized: boolean = false;
/**
 * Whether the labels should appear after or before the radio-buttons. Defaults to 'after'
 */
private _labelPosition: 'before' | 'after' = 'after';
/**
 * Whether the radio group is disabled.
 */
private _disabled: boolean = false;
/**
 * The method to be called in order to update ngModel
 */
_controlValueAccessorChangeFn: (value: any) => void = () => {};
/**
 * onTouch function registered via registerOnTouch (ControlValueAccessor).
 * \@docs-private
 */
onTouched: () => any = () => {};
/**
 * Event emitted when the group value changes.
 * Change events are only emitted when the value changes due to user interaction with
 * a radio button (the same behavior as `<input type-"radio">`).
 */
change: EventEmitter<MdRadioChange> = new EventEmitter<MdRadioChange>();
/**
 * Child radio buttons.
 */
_radios: QueryList<MdRadioButton> = null;
/**
 * Name of the radio button group. All radio buttons inside this group will use this name.
 * @return {?}
 */
get name(): string { return this._name; }
/**
 * @param {?} value
 * @return {?}
 */
set name(value: string) {
    this._name = value;
    this._updateRadioButtonNames();
  }
/**
 * Alignment of the radio-buttons relative to their labels. Can be 'before' or 'after'.
 * @deprecated
 * @return {?}
 */
get align(): 'start' | 'end' {
    // align refers to the checkbox relative to the label, while labelPosition refers to the
    // label relative to the checkbox. As such, they are inverted.
    return this.labelPosition == 'after' ? 'start' : 'end';
  }
/**
 * @param {?} v
 * @return {?}
 */
set align(v) {
    this.labelPosition = (v == 'start') ? 'after' : 'before';
  }
/**
 * Whether the labels should appear after or before the radio-buttons. Defaults to 'after'
 * @return {?}
 */
get labelPosition() {
    return this._labelPosition;
  }
/**
 * @param {?} v
 * @return {?}
 */
set labelPosition(v) {
    this._labelPosition = (v == 'before') ? 'before' : 'after';
    this._markRadiosForCheck();
  }
/**
 * Value of the radio button.
 * @return {?}
 */
get value(): any { return this._value; }
/**
 * @param {?} newValue
 * @return {?}
 */
set value(newValue: any) {
    if (this._value != newValue) {
      // Set this before proceeding to ensure no circular loop occurs with selection.
      this._value = newValue;

      this._updateSelectedRadioFromValue();
      this._checkSelectedRadioButton();
    }
  }
/**
 * @return {?}
 */
_checkSelectedRadioButton() {
    if (this.selected && !this._selected.checked) {
      this._selected.checked = true;
    }
  }
/**
 * Whether the radio button is selected.
 * @return {?}
 */
get selected() { return this._selected; }
/**
 * @param {?} selected
 * @return {?}
 */
set selected(selected: MdRadioButton) {
    this._selected = selected;
    this.value = selected ? selected.value : null;
    this._checkSelectedRadioButton();
  }
/**
 * Whether the radio group is diabled
 * @return {?}
 */
get disabled() { return this._disabled; }
/**
 * @param {?} value
 * @return {?}
 */
set disabled(value) {
    this._disabled = value;
    this._markRadiosForCheck();
  }
/**
 * @param {?} _changeDetector
 */
constructor(private _changeDetector: ChangeDetectorRef) {
    super();
  }
/**
 * Initialize properties once content children are available.
 * This allows us to propagate relevant attributes to associated buttons.
 * @return {?}
 */
ngAfterContentInit() {
    // Mark this component as initialized in AfterContentInit because the initial value can
    // possibly be set by NgModel on MdRadioGroup, and it is possible that the OnInit of the
    // NgModel occurs *after* the OnInit of the MdRadioGroup.
    this._isInitialized = true;
  }
/**
 * Mark this group as being "touched" (for ngModel). Meant to be called by the contained
 * radio buttons upon their blur.
 * @return {?}
 */
_touch() {
    if (this.onTouched) {
      this.onTouched();
    }
  }
/**
 * @return {?}
 */
private _updateRadioButtonNames(): void {
    if (this._radios) {
      this._radios.forEach(radio => {
        radio.name = this.name;
      });
    }
  }
/**
 * Updates the `selected` radio button from the internal _value state.
 * @return {?}
 */
private _updateSelectedRadioFromValue(): void {
    // If the value already matches the selected radio, do nothing.
    let /** @type {?} */ isAlreadySelected = this._selected != null && this._selected.value == this._value;

    if (this._radios != null && !isAlreadySelected) {
      this._selected = null;
      this._radios.forEach(radio => {
        radio.checked = this.value == radio.value;
        if (radio.checked) {
          this._selected = radio;
        }
      });
    }
  }
/**
 * Dispatch change event with current selection and group value.
 * @return {?}
 */
_emitChangeEvent(): void {
    if (this._isInitialized) {
      let /** @type {?} */ event = new MdRadioChange();
      event.source = this._selected;
      event.value = this._value;
      this.change.emit(event);
    }
  }
/**
 * @return {?}
 */
_markRadiosForCheck() {
    if (this._radios) {
      this._radios.forEach(radio => radio._markForCheck());
    }
  }
/**
 * Sets the model value. Implemented as part of ControlValueAccessor.
 * @param {?} value
 * @return {?}
 */
writeValue(value: any) {
    this.value = value;
    this._changeDetector.markForCheck();
  }
/**
 * Registers a callback to be triggered when the model value changes.
 * Implemented as part of ControlValueAccessor.
 * @param {?} fn Callback to be registered.
 * @return {?}
 */
registerOnChange(fn: (value: any) => void) {
    this._controlValueAccessorChangeFn = fn;
  }
/**
 * Registers a callback to be triggered when the control is touched.
 * Implemented as part of ControlValueAccessor.
 * @param {?} fn Callback to be registered.
 * @return {?}
 */
registerOnTouched(fn: any) {
    this.onTouched = fn;
  }
/**
 * Sets the disabled state of the control. Implemented as a part of ControlValueAccessor.
 * @param {?} isDisabled Whether the control should be disabled.
 * @return {?}
 */
setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this._changeDetector.markForCheck();
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'md-radio-group, mat-radio-group',
  providers: [MD_RADIO_GROUP_CONTROL_VALUE_ACCESSOR],
  host: {
    'role': 'radiogroup',
    'class': 'mat-radio-group',
  },
  inputs: ['disabled'],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'change': [{ type: Output },],
'_radios': [{ type: ContentChildren, args: [forwardRef(() => MdRadioButton), ] },],
'name': [{ type: Input },],
'align': [{ type: Input },],
'labelPosition': [{ type: Input },],
'value': [{ type: Input },],
'selected': [{ type: Input },],
'disabled': [{ type: Input },],
};
}

function MdRadioGroup_tsickle_Closure_declarations() {
/** @type {?} */
MdRadioGroup.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdRadioGroup.ctorParameters;
/** @type {?} */
MdRadioGroup.propDecorators;
/**
 * Selected value for group. Should equal the value of the selected radio button if there *is*
 * a corresponding radio button with a matching value. If there is *not* such a corresponding
 * radio button, this value persists to be applied in case a new radio button is added with a
 * matching value.
 * @type {?}
 */
MdRadioGroup.prototype._value;
/**
 * The HTML name attribute applied to radio buttons in this group.
 * @type {?}
 */
MdRadioGroup.prototype._name;
/**
 * The currently selected radio button. Should match value.
 * @type {?}
 */
MdRadioGroup.prototype._selected;
/**
 * Whether the `value` has been set to its initial value.
 * @type {?}
 */
MdRadioGroup.prototype._isInitialized;
/**
 * Whether the labels should appear after or before the radio-buttons. Defaults to 'after'
 * @type {?}
 */
MdRadioGroup.prototype._labelPosition;
/**
 * Whether the radio group is disabled.
 * @type {?}
 */
MdRadioGroup.prototype._disabled;
/**
 * The method to be called in order to update ngModel
 * @type {?}
 */
MdRadioGroup.prototype._controlValueAccessorChangeFn;
/**
 * onTouch function registered via registerOnTouch (ControlValueAccessor).
 * \@docs-private
 * @type {?}
 */
MdRadioGroup.prototype.onTouched;
/**
 * Event emitted when the group value changes.
 * Change events are only emitted when the value changes due to user interaction with
 * a radio button (the same behavior as `<input type-"radio">`).
 * @type {?}
 */
MdRadioGroup.prototype.change;
/**
 * Child radio buttons.
 * @type {?}
 */
MdRadioGroup.prototype._radios;
/** @type {?} */
MdRadioGroup.prototype._changeDetector;
}

export class MdRadioButtonBase {
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 */
constructor(public _renderer: Renderer2,
public _elementRef: ElementRef) {}
}

function MdRadioButtonBase_tsickle_Closure_declarations() {
/** @type {?} */
MdRadioButtonBase.prototype._renderer;
/** @type {?} */
MdRadioButtonBase.prototype._elementRef;
}

// As per Material design specifications the selection control radio should use the accent color
// palette by default. https://material.io/guidelines/components/selection-controls.html
export const /** @type {?} */ _MdRadioButtonMixinBase = mixinColor(MdRadioButtonBase, 'accent');
/**
 * A radio-button. May be inside of
 */
export class MdRadioButton extends _MdRadioButtonMixinBase
    implements OnInit, AfterViewInit, OnDestroy, CanColor {
/**
 * The unique ID for the radio button.
 */
id: string = `md-radio-${_uniqueIdCounter++}`;
/**
 * Analog to HTML 'name' attribute used to group radios for unique selection.
 */
name: string;
/**
 * Used to set the 'aria-label' attribute on the underlying input element.
 */
ariaLabel: string;
/**
 * The 'aria-labelledby' attribute takes precedence as the element's text alternative.
 */
ariaLabelledby: string;
/**
 * Whether the ripple effect for this radio button is disabled.
 * @return {?}
 */
get disableRipple(): boolean { return this._disableRipple; }
/**
 * @param {?} value
 * @return {?}
 */
set disableRipple(value) { this._disableRipple = coerceBooleanProperty(value); }
/**
 * Whether this radio button is checked.
 * @return {?}
 */
get checked(): boolean {
    return this._checked;
  }
/**
 * @param {?} newCheckedState
 * @return {?}
 */
set checked(newCheckedState: boolean) {
    if (this._checked != newCheckedState) {
      this._checked = newCheckedState;

      if (newCheckedState && this.radioGroup && this.radioGroup.value != this.value) {
        this.radioGroup.selected = this;
      } else if (!newCheckedState && this.radioGroup && this.radioGroup.value == this.value) {
        // When unchecking the selected radio button, update the selected radio
        // property on the group.
        this.radioGroup.selected = null;
      }

      if (newCheckedState) {
        // Notify all radio buttons with the same name to un-check.
        this._radioDispatcher.notify(this.id, this.name);
      }
      this._changeDetector.markForCheck();
    }
  }
/**
 * The value of this radio button.
 * @return {?}
 */
get value(): any {
    return this._value;
  }
/**
 * @param {?} value
 * @return {?}
 */
set value(value: any) {
    if (this._value != value) {
      this._value = value;
      if (this.radioGroup != null) {
        if (!this.checked) {
          // Update checked when the value changed to match the radio group's value
          this.checked = this.radioGroup.value == value;
        }
        if (this.checked) {
          this.radioGroup.selected = this;
        }
      }
    }
  }
/**
 * Whether or not the radio-button should appear before or after the label.
 * @deprecated
 * @return {?}
 */
get align(): 'start' | 'end' {
    // align refers to the checkbox relative to the label, while labelPosition refers to the
    // label relative to the checkbox. As such, they are inverted.
    return this.labelPosition == 'after' ? 'start' : 'end';
  }
/**
 * @param {?} v
 * @return {?}
 */
set align(v) {
    this.labelPosition = (v == 'start') ? 'after' : 'before';
  }
private _labelPosition: 'before' | 'after';
/**
 * Whether the label should appear after or before the radio button. Defaults to 'after'
 * @return {?}
 */
get labelPosition(): 'before' | 'after' {
    return this._labelPosition || (this.radioGroup && this.radioGroup.labelPosition) || 'after';
  }
/**
 * @param {?} value
 * @return {?}
 */
set labelPosition(value) {
    this._labelPosition = value;
  }
/**
 * Whether the radio button is disabled.
 * @return {?}
 */
get disabled(): boolean {
    return this._disabled || (this.radioGroup != null && this.radioGroup.disabled);
  }
/**
 * @param {?} value
 * @return {?}
 */
set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
  }
/**
 * Event emitted when the checked state of this radio button changes.
 * Change events are only emitted when the value changes due to user interaction with
 * the radio button (the same behavior as `<input type-"radio">`).
 */
change: EventEmitter<MdRadioChange> = new EventEmitter<MdRadioChange>();
/**
 * The parent radio group. May or may not be present.
 */
radioGroup: MdRadioGroup;
/**
 * ID of the native input element inside `<md-radio-button>`
 * @return {?}
 */
get inputId(): string {
    return `${this.id}-input`;
  }
/**
 * Whether this radio is checked.
 */
private _checked: boolean = false;
/**
 * Whether this radio is disabled.
 */
private _disabled: boolean;
/**
 * Value assigned to this radio.
 */
private _value: any = null;
/**
 * Whether the ripple effect on click should be disabled.
 */
private _disableRipple: boolean;
/**
 * The child ripple instance.
 */
_ripple: MdRipple;
/**
 * Reference to the current focus ripple.
 */
private _focusRipple: RippleRef;
/**
 * The native `<input type=radio>` element
 */
_inputElement: ElementRef;
/**
 * @param {?} radioGroup
 * @param {?} elementRef
 * @param {?} renderer
 * @param {?} _changeDetector
 * @param {?} _focusOriginMonitor
 * @param {?} _radioDispatcher
 */
constructor( radioGroup: MdRadioGroup,
              elementRef: ElementRef,
              renderer: Renderer2,
private _changeDetector: ChangeDetectorRef,
private _focusOriginMonitor: FocusOriginMonitor,
private _radioDispatcher: UniqueSelectionDispatcher) {
    super(renderer, elementRef);

    // Assertions. Ideally these should be stripped out by the compiler.
    // TODO(jelbourn): Assert that there's no name binding AND a parent radio group.
    this.radioGroup = radioGroup;

    _radioDispatcher.listen((id: string, name: string) => {
      if (id != this.id && name == this.name) {
        this.checked = false;
      }
    });
  }
/**
 * Focuses the radio button.
 * @return {?}
 */
focus(): void {
    this._focusOriginMonitor.focusVia(this._inputElement.nativeElement, 'keyboard');
  }
/**
 * Marks the radio button as needing checking for change detection.
 * This method is exposed because the parent radio group will directly
 * update bound properties of the radio button.
 * @return {?}
 */
_markForCheck() {
    // When group value changes, the button will not be notified. Use `markForCheck` to explicit
    // update radio button's status
    this._changeDetector.markForCheck();
  }
/**
 * @return {?}
 */
ngOnInit() {
    if (this.radioGroup) {
      // If the radio is inside a radio group, determine if it should be checked
      this.checked = this.radioGroup.value === this._value;
      // Copy name from parent radio group
      this.name = this.radioGroup.name;
    }
  }
/**
 * @return {?}
 */
ngAfterViewInit() {
    this._focusOriginMonitor
      .monitor(this._inputElement.nativeElement, this._renderer, false)
      .subscribe(focusOrigin => this._onInputFocusChange(focusOrigin));
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._focusOriginMonitor.stopMonitoring(this._inputElement.nativeElement);
  }
/**
 * Dispatch change event with current value.
 * @return {?}
 */
private _emitChangeEvent(): void {
    let /** @type {?} */ event = new MdRadioChange();
    event.source = this;
    event.value = this._value;
    this.change.emit(event);
  }
/**
 * @return {?}
 */
_isRippleDisabled() {
    return this.disableRipple || this.disabled;
  }
/**
 * @param {?} event
 * @return {?}
 */
_onInputClick(event: Event) {
    // We have to stop propagation for click events on the visual hidden input element.
    // By default, when a user clicks on a label element, a generated click event will be
    // dispatched on the associated input element. Since we are using a label element as our
    // root container, the click event on the `radio-button` will be executed twice.
    // The real click event will bubble up, and the generated click event also tries to bubble up.
    // This will lead to multiple click events.
    // Preventing bubbling for the second event will solve that issue.
    event.stopPropagation();
  }
/**
 * Triggered when the radio button received a click or the input recognized any change.
 * Clicking on a label element, will trigger a change event on the associated input.
 * @param {?} event
 * @return {?}
 */
_onInputChange(event: Event) {
    // We always have to stop propagation on the change event.
    // Otherwise the change event, from the input element, will bubble up and
    // emit its event object to the `change` output.
    event.stopPropagation();

    let /** @type {?} */ groupValueChanged = this.radioGroup && this.value != this.radioGroup.value;
    this.checked = true;
    this._emitChangeEvent();

    if (this.radioGroup) {
      this.radioGroup._controlValueAccessorChangeFn(this.value);
      this.radioGroup._touch();
      if (groupValueChanged) {
        this.radioGroup._emitChangeEvent();
      }
    }
  }
/**
 * Function is called whenever the focus changes for the input element.
 * @param {?} focusOrigin
 * @return {?}
 */
private _onInputFocusChange(focusOrigin: FocusOrigin) {
    if (!this._focusRipple && focusOrigin === 'keyboard') {
      this._focusRipple = this._ripple.launch(0, 0, {persistent: true, centered: true});
    } else if (!focusOrigin) {
      if (this.radioGroup) {
        this.radioGroup._touch();
      }

      if (this._focusRipple) {
        this._focusRipple.fadeOut();
        this._focusRipple = null;
      }
    }
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-radio-button, mat-radio-button',
  templateUrl: 'radio.html',
  styleUrls: ['radio.css'],
  inputs: ['color'],
  encapsulation: ViewEncapsulation.None,
  host: {
    'class': 'mat-radio-button',
    '[class.mat-radio-checked]': 'checked',
    '[class.mat-radio-disabled]': 'disabled',
    '[attr.id]': 'id',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdRadioGroup, decorators: [{ type: Optional }, ]},
{type: ElementRef, },
{type: Renderer2, },
{type: ChangeDetectorRef, },
{type: FocusOriginMonitor, },
{type: UniqueSelectionDispatcher, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'id': [{ type: Input },],
'name': [{ type: Input },],
'ariaLabel': [{ type: Input, args: ['aria-label', ] },],
'ariaLabelledby': [{ type: Input, args: ['aria-labelledby', ] },],
'disableRipple': [{ type: Input },],
'checked': [{ type: Input },],
'value': [{ type: Input },],
'align': [{ type: Input },],
'labelPosition': [{ type: Input },],
'disabled': [{ type: Input },],
'change': [{ type: Output },],
'_ripple': [{ type: ViewChild, args: [MdRipple, ] },],
'_inputElement': [{ type: ViewChild, args: ['input', ] },],
};
}

function MdRadioButton_tsickle_Closure_declarations() {
/** @type {?} */
MdRadioButton.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdRadioButton.ctorParameters;
/** @type {?} */
MdRadioButton.propDecorators;
/**
 * The unique ID for the radio button.
 * @type {?}
 */
MdRadioButton.prototype.id;
/**
 * Analog to HTML 'name' attribute used to group radios for unique selection.
 * @type {?}
 */
MdRadioButton.prototype.name;
/**
 * Used to set the 'aria-label' attribute on the underlying input element.
 * @type {?}
 */
MdRadioButton.prototype.ariaLabel;
/**
 * The 'aria-labelledby' attribute takes precedence as the element's text alternative.
 * @type {?}
 */
MdRadioButton.prototype.ariaLabelledby;
/** @type {?} */
MdRadioButton.prototype._labelPosition;
/**
 * Event emitted when the checked state of this radio button changes.
 * Change events are only emitted when the value changes due to user interaction with
 * the radio button (the same behavior as `<input type-"radio">`).
 * @type {?}
 */
MdRadioButton.prototype.change;
/**
 * The parent radio group. May or may not be present.
 * @type {?}
 */
MdRadioButton.prototype.radioGroup;
/**
 * Whether this radio is checked.
 * @type {?}
 */
MdRadioButton.prototype._checked;
/**
 * Whether this radio is disabled.
 * @type {?}
 */
MdRadioButton.prototype._disabled;
/**
 * Value assigned to this radio.
 * @type {?}
 */
MdRadioButton.prototype._value;
/**
 * Whether the ripple effect on click should be disabled.
 * @type {?}
 */
MdRadioButton.prototype._disableRipple;
/**
 * The child ripple instance.
 * @type {?}
 */
MdRadioButton.prototype._ripple;
/**
 * Reference to the current focus ripple.
 * @type {?}
 */
MdRadioButton.prototype._focusRipple;
/**
 * The native `<input type=radio>` element
 * @type {?}
 */
MdRadioButton.prototype._inputElement;
/** @type {?} */
MdRadioButton.prototype._changeDetector;
/** @type {?} */
MdRadioButton.prototype._focusOriginMonitor;
/** @type {?} */
MdRadioButton.prototype._radioDispatcher;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
